import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import useStore from './store';

import AppLayout       from './components/Layout/AppLayout';
import LoginPage       from './pages/Auth/LoginPage';
import AuthCallback    from './pages/Auth/AuthCallback';
import Dashboard       from './pages/Dashboard/Dashboard';
import AttendancePage  from './pages/Attendance/AttendancePage';
import LeavesPage      from './pages/Leaves/LeavesPage';
import HolidaysPage    from './pages/Holidays/HolidaysPage';
import UserProfilePage from './pages/UserProfile/UserProfilePage';
import AdminUsersPage  from './pages/Admin/AdminUsersPage';
import AdminLossOfPayReportPage from './pages/Admin/AdminLossOfPayReportPage';
import AdminLeavePoliciesPage from './pages/Admin/AdminLeavePoliciesPage';
function LoadingScreen({ message = 'Loading…' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0A0612', flexDirection: 'column', gap: 16,
    }}>
      <div style={{
        width: 44, height: 44,
        border: '3px solid rgba(124,58,237,.25)',
        borderTop: '3px solid #7C3AED',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite',
      }} />
      <div style={{ fontSize: '0.875rem', color: 'rgba(159,103,255,.6)' }}>
        {message}
      </div>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}

function RequireAuth({ children, roles }) {
  const { user, token, authLoading } = useStore();
  const location = useLocation();

  if (authLoading) {
    return <LoadingScreen message="Loading your profile…" />;
  }

  if (!token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AuthInit({ children }) {
  const { token, fetchMe, user } = useStore();

  useEffect(() => {
    if (token && !user) {
      fetchMe();
    } else {
      useStore.setState({ authLoading: false });
    }
  }, []);

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthInit>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'DM Sans, sans-serif',
              fontSize:   '0.875rem',
              borderRadius: '10px',
              boxShadow:  '0 8px 24px rgba(0,0,0,.12)',
            },
            success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
          }}
        />

        <Routes>
          <Route path="/login"         element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard"  element={<Dashboard />} />
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/leaves"     element={<LeavesPage />} />
            <Route path="/holidays"   element={<HolidaysPage />} />
            <Route path="/profile"    element={<UserProfilePage />} />

            <Route path="/team/attendance" element={<RequireAuth roles={['admin','manager']}><AttendancePage teamView /></RequireAuth>} />
            <Route path="/team/leaves"     element={<RequireAuth roles={['admin','manager']}><LeavesPage teamView /></RequireAuth>} />
            <Route path="/team/overview"   element={<RequireAuth roles={['admin','manager']}><TeamOverviewPage /></RequireAuth>} />

            <Route path="/admin/users"         element={<RequireAuth roles={['admin']}><AdminUsersPage /></RequireAuth>} />
            <Route path="/admin/users/:userId" element={<RequireAuth roles={['admin']}><AdminUserEditPage /></RequireAuth>} />
            <Route path="/admin/holidays"      element={<RequireAuth roles={['admin']}><HolidaysPage /></RequireAuth>} />
            <Route path="/admin/leave-policies" element={<RequireAuth roles={['admin']}><AdminLeavePoliciesPage /></RequireAuth>} />
            <Route path="/admin/report"         element={<RequireAuth roles={['admin']}><AdminLossOfPayReportPage /></RequireAuth>} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthInit>
    </BrowserRouter>
  );
}

function TeamOverviewPage() {
  const [users, setUsers]     = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { user }              = useStore();

  React.useEffect(() => {
    if (!user?.id) return;
    import('./services/api').then(({ usersApi }) => {
      usersApi.getReportees(user.id)
        .then((res) => setUsers(res.data.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    });
  }, [user?.id]);

  if (loading) return <LoadingScreen message="Loading team…" />;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Team Overview</div>
          <div className="page-subtitle">Your direct and indirect reports</div>
        </div>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-title">No reportees found</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Designation</th>
                  <th>Department</th><th>Email</th><th>Manager</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar avatar-sm">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <span style={{ fontWeight: 600 }}>
                          {u.first_name} {u.last_name}
                        </span>
                      </div>
                    </td>
                    <td><span className="badge badge-violet">{u.role}</span></td>
                    <td style={{ fontSize: '0.875rem' }}>{u.designation || '—'}</td>
                    <td style={{ fontSize: '0.875rem' }}>{u.department?.name || '—'}</td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{u.email}</td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {u.manager ? `${u.manager.first_name} ${u.manager.last_name}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminUserEditPage() {
  const { userId } = require('react-router-dom').useParams();
  return <UserProfilePage viewUserId={userId} />;
}